import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import EquipmentServiceHistoryGrid from '../../../../components/service/equipmentServiceHistoryGrid/equipmentServiceHistoryGrid.component';
import CustomCircularProgress from '../../../../components/ui/customCircularProgress/customCircularProgress.component';
import { JOBS_ENDPOINT } from '../../../../consts';
import { useHasPermission } from '../../../../contexts/auth';
import { useCRUD } from '../../../../api/crud';
import { Job } from '../../../../types/jobs';
import { Equipment } from '../../../../types/equipments';
import { useBreadcrumbs } from '../../../../components/ui/breadcrumps/breadcrumbsContext';

const getUniqueEquipmentsFromJob = (job: Job | null): Equipment[] => {
  if (!job) {
    return [];
  }

  const tasks = job.tasks ?? job.services ?? [];
  const seen = new Set<string>();
  const equipments: Equipment[] = [];

  for (const task of tasks) {
    if (task.equipment?.id && !seen.has(task.equipment.id)) {
      seen.add(task.equipment.id);
      equipments.push(task.equipment);
    }
  }

  return equipments;
};

export const JobEquipmentsServiceHistory = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { setOverride, clearOverride } = useBreadcrumbs();
  const { get } = useCRUD(JOBS_ENDPOINT);
  const canViewAllJobs = useHasPermission("service.view_job");
  const canViewOwnJobs = useHasPermission("service.view_own_job");
  const canViewJobs = canViewAllJobs || canViewOwnJobs;

  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedEquipmentId, setSelectedEquipmentId] = useState<string | null>(null);

  const equipments = useMemo(() => getUniqueEquipmentsFromJob(job), [job]);

  useEffect(() => {
    if (!id) {
      return undefined;
    }

    const historyPath = `/service/jobs/${id}/service-history`;
    setOverride(historyPath, t('Service History'));

    return () => {
      clearOverride(historyPath);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, t]);

  useEffect(() => {
    const fetchJob = async () => {
      if (!id) {
        setJob(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      const response = await get(id);
      setJob(response.data ?? null);
      setLoading(false);
    };

    const handleJobUpdated = () => {
      fetchJob();
    };

    window.addEventListener('jobUpdated', handleJobUpdated);
    fetchJob();

    return () => {
      window.removeEventListener('jobUpdated', handleJobUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (equipments.length === 0) {
      setSelectedEquipmentId(null);
      return;
    }

    setSelectedEquipmentId((current) => {
      if (current && equipments.some((equipment) => equipment.id === current)) {
        return current;
      }
      return equipments[0].id;
    });
  }, [equipments]);

  if (!canViewJobs) {
    return null;
  }

  if (loading) {
    return <CustomCircularProgress />;
  }

  if (equipments.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t('No equipment assigned to this job.')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, mr: 0.5 }}>
          {t('Equipment')}:
        </Typography>
        {equipments.map((equipment) => {
          const isSelected = equipment.id === selectedEquipmentId;
          return (
            <Chip
              key={equipment.id}
              label={equipment.full_name || equipment.id}
              clickable
              color={isSelected ? 'primary' : 'default'}
              variant={isSelected ? 'filled' : 'outlined'}
              onClick={() => setSelectedEquipmentId(equipment.id)}
            />
          );
        })}
      </Box>
      {selectedEquipmentId && (
        <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
          <EquipmentServiceHistoryGrid
            equipmentId={selectedEquipmentId}
            title={t('Service History')}
            gridSettingsKey="JOB_EQUIPMENT_SERVICE_HISTORY"
            refreshEventName="equipmentJobAdded"
          />
        </Box>
      )}
    </Box>
  );
};
